import {
  Component,
  OnInit,
  inject,
  DestroyRef,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription, lastValueFrom, interval, switchMap, from } from 'rxjs';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { WindowService } from 'src/shared/providers/window.service';
import { Util } from 'src/shared/util/util';
import { Client } from 'src/providers/client/client';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';
import { SelectModel } from 'src/tidy-ui-components/components/form/select/select.model';

import { ConciergeApiService, CreateConciergeItemData } from 'src/shared/services/concierge-api.service';
import { MarketingPricingService } from 'src/shared/services/marketing-pricing.service';

import { PricingCalendarComponent } from 'src/pages/marketing/components/property-pricing/pricing-calendar.component';
import { PricingCustomizationComponent } from 'src/pages/marketing/components/property-pricing/pricing-customization.component';
import { PricingNeighborhoodComponent } from 'src/pages/marketing/components/property-pricing/pricing-neighborhood.component';

@Component({
  selector: 'edit-pricing-page',
  templateUrl: 'edit-pricing.page.html',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, TidyUiComponentsModule, SharedModule,
    PricingCalendarComponent, PricingCustomizationComponent, PricingNeighborhoodComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class EditPricingPage implements OnInit {
  private readonly conciergeApi = inject(ConciergeApiService);
  private readonly pricingService = inject(MarketingPricingService);
  private readonly client = inject(Client);
  private readonly fb = inject(FormBuilder);
  private readonly navCtrl = inject(CustomNavController);
  private readonly rightSidePanelService = inject(RightSidePanelService);
  private readonly storage = inject(TidyStorage);
  private readonly util = inject(Util);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  readonly windowService = inject(WindowService);

  isRightSideContent = true;
  loading = true;
  addresses: any[] = [];
  addressSelectItems: SelectModel[] = [];
  selectedAddress: any = null;
  submitted = false;
  errorMessage = '';
  fromGettingStarted = false;
  state: 'form' | 'pending' | 'review' = 'form';

  innerTabs = [
    { value: 'calendar', label: 'Calendar' },
    { value: 'customization', label: 'Customization' },
    { value: 'neighborhood', label: 'Neighborhood Data' }
  ];
  selectedInnerTab = 'calendar';
  tabAreaLoading = false;
  private pollingSubscription: Subscription | null = null;

  form: FormGroup = this.fb.group({
    addressId: ['', Validators.required],
    cleaningFee: ['', Validators.required],
    otherFees: ['0'],
    minRate: ['', Validators.required],
    maxRate: [''],
    minimumStay: ['', Validators.required],
    maximumStay: ['', Validators.required],
    baseRate: [''],
    otherRequirements: [''],
    overrides: this.fb.array([])
  });

  boundSubmit = this.submit.bind(this);

  get overrides(): FormArray {
    return this.form.get('overrides') as FormArray;
  }

  async ngOnInit() {
    this.isRightSideContent = (await this.storage.retrieve('dialog-right-side-open')) || false;

    const dialogParams = await this.storage.retrieve('dialog-params');
    const passedAddress = dialogParams?.address || this.navCtrl.getParam('address', false);
    this.fromGettingStarted = !!dialogParams?.fromGettingStarted;
    const passedAddressId = dialogParams?.addressId ?? this.navCtrl.getParam('addressId', false);

    this.rightSidePanelService.setDialogPageTitle(
      this.fromGettingStarted ? 'Add Your Pricing Guidelines' : 'Edit Pricing'
    );

    try {
      const addresses = await this.client.getAddresses();
      this.addresses = addresses || [];
      this.addressSelectItems = this.addresses.map(a => ({
        value: a.id.toString(),
        viewValue: a.address_name || a.display_name || a.address1 || 'Your Property'
      }));

      const defaultId = passedAddress?.id
        ?? (passedAddressId !== null && passedAddressId !== undefined ? Number(passedAddressId) : null)
        ?? this.client.getSelectedAddressId(this.addresses)
        ?? this.addresses[0]?.id;
      if (defaultId !== null && defaultId !== undefined) {
        this.form.get('addressId')!.setValue(defaultId.toString(), { emitEvent: false });
        this.selectedAddress = this.addresses.find(a => a.id === defaultId) || passedAddress;
        await this.loadDataForAddress(defaultId);
      }
    } catch (err) {
      console.error('Failed to load addresses', err);
    } finally {
      this.loading = false;
      this.cdr.markForCheck();
    }
  }

  async onAddressChange(value: string) {
    const id = parseFloat(value);
    if (Number.isNaN(id)) {return;}
    this.selectedAddress = this.addresses.find(a => a.id === id) || null;
    this.submitted = false;
    this.errorMessage = '';
    this.loading = true;
    this.cdr.markForCheck();
    try {
      await this.loadDataForAddress(id);
    } finally {
      this.loading = false;
      this.cdr.markForCheck();
    }
  }

  private async loadDataForAddress(id: number) {
    // Reset form fields (preserve addressId), clear overrides, then patch with fresh data.
    // BE only returns base/min/max price today; cleaningFee/otherFees/min-max stay
    // need a BE update before they can autofill from PriceLabs.
    this.form.reset({ addressId: id.toString(), otherFees: '0' }, { emitEvent: false });
    this.overrides.clear();

    let basePriceFromCustomization: number | null = null;
    try {
      const customization = await this.pricingService.getCustomization(id);
      const patch: any = {};
      if (customization?.basePrice !== null && customization?.basePrice !== undefined) {
        basePriceFromCustomization = customization.basePrice;
        patch.baseRate = (customization.basePrice / 100).toString();
      }
      if (customization?.minPrice !== null && customization?.minPrice !== undefined) {
        patch.minRate = (customization.minPrice / 100).toString();
      }
      if (customization?.maxPrice !== null && customization?.maxPrice !== undefined) {
        patch.maxRate = (customization.maxPrice / 100).toString();
      }
      this.form.patchValue(patch, { emitEvent: false });

      for (const o of customization?.overrides || []) {
        this.overrides.push(this.createOverrideGroup(
          o.date,
          o.price !== null && o.price !== undefined ? o.price / 100 : null,
          o.minStay
        ));
      }
    } catch (err) {
      console.error('Failed to load customization data', err);
    }

    // Fall back to neighborhood listing rate for baseRate when customization is missing it.
    if (basePriceFromCustomization === null || basePriceFromCustomization === undefined) {
      try {
        const neighborhood = await this.pricingService.getNeighborhoodData(id);
        if (neighborhood?.avgListingRate !== null && neighborhood?.avgListingRate !== undefined) {
          this.form.patchValue(
            { baseRate: (neighborhood.avgListingRate / 100).toString() },
            { emitEvent: false }
          );
        }
      } catch (err) {
        console.error('Failed to load neighborhood data', err);
      }
    }
  }

  private createOverrideGroup(date?: string, price?: number | null, minStay?: number | null): FormGroup {
    return this.fb.group({
      date: [date || '', Validators.required],
      price: [price ?? '', Validators.required],
      minStay: [minStay ?? '', Validators.required]
    });
  }

  addDateOverride() {
    this.overrides.push(this.createOverrideGroup());
    this.cdr.markForCheck();
  }

  removeDateOverride(index: number) {
    this.overrides.removeAt(index);
    this.cdr.markForCheck();
  }

  get addressName(): string {
    return this.selectedAddress?.address_name
      || this.selectedAddress?.display_name
      || this.selectedAddress?.address1
      || 'Your Property';
  }

  async submit() {
    this.errorMessage = '';
    this.submitted = true;
    if (!this.form.valid) {
      return;
    }
    const v = this.form.value;
    const lines: string[] = [
      `Property: ${this.addressName} (id ${v.addressId})`,
      `Cleaning Fee: ${v.cleaningFee}`,
      `Other Fees: ${v.otherFees}`,
      `Min Rate: ${v.minRate}`,
      `Max Rate: ${v.maxRate}`,
      `Minimum Stay: ${v.minimumStay}`,
      `Maximum Stay: ${v.maximumStay}`,
    ];
    if (v.baseRate) {lines.push(`Base Rate: ${v.baseRate}`);}
    if (v.otherRequirements) {lines.push(`Other Pricing Requirements: ${v.otherRequirements}`);}

    const overridesPayload = (v.overrides || []).map((o: any) => ({
      date: o.date,
      price: o.price,
      min_nights: o.minStay
    }));
    if (overridesPayload.length) {
      lines.push('');
      lines.push('Date Overrides:');
      for (const o of overridesPayload) {
        lines.push(`  - ${o.date}: $${o.price}, min ${o.min_nights} nights`);
      }
    }

    const item: CreateConciergeItemData = {
      type: 'support.other',
      data: {
        subject: `Update Pricing for ${this.addressName}`,
        body: `Please update the pricing for "${this.addressName}" with the following:\n\n${lines.join('\n')}`,
        attachments: [],
        emails: []
      },
      metadata: {
        address_id: parseFloat(v.addressId),
        address_name: this.addressName,
        cleaning_fee: v.cleaningFee,
        other_fees: v.otherFees,
        min_rate: v.minRate,
        max_rate: v.maxRate,
        minimum_stay: v.minimumStay,
        maximum_stay: v.maximumStay,
        base_rate: v.baseRate || null,
        other_pricing_requirements: v.otherRequirements || null,
        date_overrides: overridesPayload
      }
    };

    try {
      await lastValueFrom(this.conciergeApi.createConciergeItem(item));
      if (this.fromGettingStarted) {
        await this.confirmStep();
        return;
      }
      this.util.showSuccess('Pricing update request submitted');
      if (this.windowService.isDesktopRes) {
        this.rightSidePanelService.closeRightSidePanel();
      } else {
        this.navCtrl.back();
      }
    } catch (err: any) {
      this.errorMessage = err?.error?.message || err?.message || 'Failed to submit pricing update.';
      this.cdr.markForCheck();
    }
  }

  private startPolling() {
    this.pollingSubscription?.unsubscribe();
    const addressId = parseFloat(this.form.value.addressId);
    this.pollingSubscription = interval(10000).pipe(
      takeUntilDestroyed(this.destroyRef),
      switchMap(() => from(this.pricingService.getCalendarData(addressId)))
    ).subscribe({
      next: (data) => {
        if (data?.length && data[0].price !== null && data[0].price !== undefined) {
          this.pollingSubscription?.unsubscribe();
          this.state = 'review';
          this.selectedInnerTab = 'calendar';
          this.tabAreaLoading = true;
          this.cdr.markForCheck();
        }
      },
      error: () => {
        // Swallow polling errors; keep retrying on next tick
      }
    });
  }

  editPricing = () => {
    this.pollingSubscription?.unsubscribe();
    this.state = 'form';
    this.cdr.markForCheck();
  };

  selectInnerTab(tab: string) {
    this.selectedInnerTab = tab;
    this.tabAreaLoading = true;
    this.cdr.markForCheck();
  }

  onChildLoaded() {
    this.tabAreaLoading = false;
    this.cdr.markForCheck();
  }

  confirmStep = async () => {
    try {
      await this.client.addMetadata({
        key: `onboarding_step.${this.form.value.addressId}.pricing`,
        value: 'completed'
      });
    } catch {
      // Metadata update is best-effort
    }
    if (this.windowService.isDesktopRes) {
      this.rightSidePanelService.closeRightSidePanel();
    } else {
      this.navCtrl.navigateForward('getting-started');
    }
  };

  makeChanges = () => {
    this.state = 'form';
    this.cdr.markForCheck();
  };
}
